import { useCallback, useState } from 'react'
import { View, StyleSheet, FlatList } from 'react-native'
import { Modal, Portal, Text, Button, List } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { MelddSearchBar } from '../../../../../@ui/form/meldd-search.input'
import { createStyle } from '../../../../../contexts/global-styles.provider'
import {
  useSearchUserQuery,
  useTransferClusterOwnershipMutation,
  ClusterByIdQuery,
} from '../../../../../generated/graphql'
import Toast from 'react-native-root-toast'
import { MessageType } from '../../../../../utils/message-type'
import * as Sentry from '@sentry/react-native'
import { AppNavigation } from '../../../../../navigation/types'
import { useNavigation } from '@react-navigation/native'

interface TransferClusterOwnershipModalProps {
  visible: boolean
  onDismiss: () => void
  cluster: ClusterByIdQuery['clusterById']
}

export const TransferClusterOwnershipModal = ({
  visible,
  onDismiss,
  cluster,
}: TransferClusterOwnershipModalProps) => {
  const { t } = useTranslation('clusters')
  const navigation = useNavigation<AppNavigation>()
  const styles = useStyle()
  const [search, setSearch] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)

  const { data, loading, fetchMore } = useSearchUserQuery({
    variables: {
      input: {
        username: search,
        nodeId: cluster.nodeId,
        limit: 10,
        offset: 0,
      },
      nodeId: cluster.nodeId,
      clusterId: cluster.id,
    },
    skip: !search || search.length < 2,
  })

  const [transferOwnership, { loading: isTransferring }] =
    useTransferClusterOwnershipMutation()

  const users = data?.usersBySearchUsername || []

  const handleTransfer = useCallback(
    async ({ selectedUserId }: { selectedUserId: string }) => {
      return async () => {
        if (!selectedUserId) return

        try {
          await transferOwnership({
            variables: {
              input: {
                clusterId: cluster.id,
                newOwnerId: selectedUserId,
              },
            },
            refetchQueries: ['clusterById'],
          })
          navigation.navigate('ClusterCards', { clusterId: cluster.id })
        } catch (error) {
          console.error(error)
          Sentry.captureException(error)
          Toast.show((error as Error).message, MessageType.error)
        }
      }
    },
    [cluster.id, transferOwnership, t, onDismiss, navigation]
  )

  const handleCancel = () => {
    setSelectedUserId(null)
    setIsConfirming(false)
    setSearch('')
    onDismiss()
  }

  if (isConfirming && selectedUserId) {
    const selectedUser = users.find((u) => u.id === selectedUserId)
    return (
      <Portal>
        <Modal
          visible={visible}
          onDismiss={handleCancel}
          contentContainerStyle={styles.modal}
        >
          <Text variant="titleLarge" style={styles.title}>
            {t('confirm-transfer-ownership')}
          </Text>
          <Text style={styles.confirmText}>
            {t('transfer-ownership-confirm-message', {
              clusterTopic: cluster.topic,
              username: selectedUser?.username,
            })}
          </Text>
          <View style={styles.buttonContainer}>
            <Button
              mode="outlined"
              onPress={handleCancel}
              style={styles.button}
            >
              {t('cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={() => handleTransfer({ selectedUserId: selectedUserId })}
              loading={isTransferring}
              disabled={isTransferring}
              style={styles.button}
            >
              {t('confirm')}
            </Button>
          </View>
        </Modal>
      </Portal>
    )
  }

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={handleCancel}
        contentContainerStyle={styles.modal}
      >
        <Text variant="titleLarge" style={styles.title}>
          {t('transfer-ownership')}
        </Text>

        <MelddSearchBar
          placeholder={t('search-users')}
          value={search}
          onChangeText={setSearch}
        />

        <View style={styles.listContainer}>
          {search.length < 2 ? (
            <Text style={styles.hint}>{t('type-to-search')}</Text>
          ) : (
            <FlatList
              data={users}
              renderItem={({ item }) => (
                <List.Item
                  title={item.username}
                  left={(props) => <List.Icon {...props} icon="account" />}
                  onPress={() => {
                    setSelectedUserId(item.id)
                    setIsConfirming(true)
                  }}
                />
              )}
              keyExtractor={(item) => item.id}
              onEndReached={() => {
                if (!loading && users.length > 0) {
                  fetchMore({
                    variables: {
                      input: {
                        username: search,
                        nodeId: cluster.nodeId,
                        limit: 20,
                        offset: users.length,
                      },
                    },
                  })
                }
              }}
            />
          )}
        </View>

        <Button
          mode="outlined"
          onPress={handleCancel}
          style={styles.cancelButton}
        >
          {t('cancel')}
        </Button>
      </Modal>
    </Portal>
  )
}

const useStyle = createStyle(({ theme }) =>
  StyleSheet.create({
    modal: {
      backgroundColor: theme.colors.surface,
      padding: theme.spacing(4),
      margin: theme.spacing(4),
      borderRadius: theme.spacing(2),
      maxHeight: '80%',
    },
    title: {
      marginBottom: theme.spacing(3),
      textAlign: 'center',
    },
    listContainer: {
      marginTop: theme.spacing(3),
      maxHeight: 400,
    },
    hint: {
      textAlign: 'center',
      color: theme.colors.onSurfaceVariant,
      marginTop: theme.spacing(2),
    },
    cancelButton: {
      marginTop: theme.spacing(3),
    },
    confirmText: {
      marginVertical: theme.spacing(3),
      textAlign: 'center',
    },
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
    },
    button: {
      flex: 1,
    },
  })
)
